import { Box, Dialog, DialogContent, Typography } from "@mui/material";
import React from "react";
import { useLocaleState, useTranslate } from "react-admin";

import { IntegrationExecution } from "../generated/graphql";

interface ExecutionDetailsModalProps {
  open: boolean;
  onClose: () => void;
  execution: IntegrationExecution;
}

const ExecutionDetailsModalContent = ({ execution }: { execution: IntegrationExecution }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const summary = execution.summary as {
    processed?: Record<string, number>;
    failed?: Record<string, Array<{ id: string; reason: string }>>;
    errors?: {
      system?: Array<{ operation: string; reason: string; timestamp: string }>;
      warnings?: Array<{ operation: string; reason: string; timestamp: string }>;
    };
  };

  if (summary?.processed || summary?.failed || summary?.errors) {
    const processedEntries = Object.entries(summary.processed || {}).filter(([_, count]) => count > 0);
    const failedEntries = Object.entries(summary.failed || {}).filter(([_, items]) => items?.length > 0);
    const systemErrors = summary?.errors?.system || [];
    const warnings = summary?.errors?.warnings || [];

    return (
      <Box>
        {processedEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" color="success.main">
              {translate("integration.execution.details.processed")}
            </Typography>
            {processedEntries.map(([type, count]) => (
              <Typography key={type} variant="body2" sx={{ ml: 2 }}>
                • {count} {translate(`integration.execution.entityTypes.${type}`, { _: type })}
              </Typography>
            ))}
          </Box>
        )}

        {systemErrors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" color="error.main">
              {translate("integration.execution.details.systemErrors")} ({systemErrors.length}):
            </Typography>
            {systemErrors.map((error, index) => (
              <Box key={index} sx={{ ml: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  • <strong>{error.operation}</strong>: {error.reason}
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, fontSize: "0.7rem", color: "text.secondary" }}>
                  {new Date(error.timestamp).toLocaleString(locale)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {warnings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" color="warning.main">
              {translate("integration.execution.details.warnings")} ({warnings.length}):
            </Typography>
            {warnings.map((warning, index) => (
              <Box key={index} sx={{ ml: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  • <strong>{warning.operation}</strong>: {warning.reason}
                </Typography>
                <Typography variant="body2" sx={{ ml: 2, fontSize: "0.7rem", color: "text.secondary" }}>
                  {new Date(warning.timestamp).toLocaleString(locale)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {failedEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" color="warning.main">
              {translate("integration.execution.details.failed")} ({failedEntries.length}):
            </Typography>
            {failedEntries.map(([type, items]) => (
              <Box key={type} sx={{ ml: 2, mb: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {translate(`integration.execution.entityTypes.${type}`, { _: type })} ({items.length}{" "}
                  {translate("integration.execution.details.failed")}):
                </Typography>
                {items.map((item, index) => (
                  <Typography key={index} variant="body2" sx={{ ml: 2, fontSize: "0.75rem" }}>
                    • {item.id}: {item.reason}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        )}

        {processedEntries.length === 0 &&
          failedEntries.length === 0 &&
          systemErrors.length === 0 &&
          warnings.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate("integration.execution.details.noEntitiesProcessed")}
            </Typography>
          )}
      </Box>
    );
  } else {
    return (
      <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap" }}>
        {JSON.stringify(execution.summary, null, 2)}
      </Typography>
    );
  }
};

export const ExecutionDetailsModal: React.FC<ExecutionDetailsModalProps> = ({ open, onClose, execution }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        {execution && execution.summary && <ExecutionDetailsModalContent execution={execution} />}
      </DialogContent>
    </Dialog>
  );
};
